import React, { useEffect, useState } from 'react';
import { ProbingForm as UIProbingForm, toast } from '@shopee/chatbot-ui';
import { BUYER_ORDER_LIST_TITLE_TEXT, INPUT_TYPE, PROBING_FORM_EXP, PROBING_FORM_TRANSIFY, SELECT_ORDER_CAN_NOT_FIND_TEXT, SELECT_ORDER_TEXT, SELLER_ORDER_LIST_TITLE_TEXT, TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE, USER_TYPE_INDEX } from '@/utils/constants';
import { getTrans, replaceParams } from '@/utils/tools';
import { formatOrder, oldTaskbotLoadMore } from '../PropsProtocol/OrderList';
import { EnquiryItemType } from '@/components/EnquiryList';
import { ProbingFormValues } from '@shopee/chatbot-ui/lib/components/ProbingForm';
import { ProbingFormFile, ProbingFormUpload } from '@/components/ProbingFormUpload';
import { handleRequest } from '@/utils';
import reporter from '@/utils/report';
import { inject, observer } from 'mobx-react';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { InitStoreType } from '@/types/store/init';
import { InputStoreType } from '@/types/store/input';
import { getProbingFormExperiment } from '@/utils/getProbingFormExperiment';
import { FirstInitStore } from '@/stores/firstInit';
import API from '@/services';
import show from './show';

export interface ProbingFormProps {
  showUserAndEnquiryType: boolean;
  showOrderSelector: boolean;
  onClose: () => void;
  onSubmit: (params: SubmitProbingFormParams) => void;
  dataSource: EnquiryItemType[];
  initStore?: InitStoreType;
  inputStore?: InputStoreType;
  firstInit?: FirstInitStore;
  dialogueId?: string;
  autoQueue?: {
    userType?: {
      value: number;
      content: string;
    };
    enquiryType?: {
      value: number;
      content: string;
    };
    order?: {
      orderSn: string;
    };
  }
}

export interface SubmitProbingFormParams {
  user_type: {
    id: number,
    name: string,
  },
  enquiry_type: {
    id: number,
    name: string,
  },
  problem_type?: {
    id: number,
    name: string,
  },
  order_id: string,
  description: string,
  files: ProbingFormFile[],
}

const DEFAULT_MIN_DESC_LENGTH = 50;
const DEFAULT_MAX_DESC_LENGTH = 500;

const ProbingForm = inject(getChildInject('firstInit', 'initStore', 'inputStore'))(observer((props: ProbingFormProps) => {
  const { showOrderSelector, showUserAndEnquiryType } = props;
  const [maxDescLength, setMaxDescLength] = useState<number>(DEFAULT_MAX_DESC_LENGTH);
  const [minDescLength, setMinDescLength] = useState<number>(DEFAULT_MIN_DESC_LENGTH);

  const loadMoreOrder = async (loadMoreProps: {
    page_num: number;
    page_size: number;
    status: number;
    user_type: number;
  }) => {
    return await oldTaskbotLoadMore(loadMoreProps, {
      is_seller: loadMoreProps.user_type === USER_TYPE_INDEX.Seller,
      sessionId: props.initStore?.sessionId as string,
    });
  };

  const getInitialValues = async (): Promise<any> => {
    const params = { session_id: props.initStore?.sessionId };
    let autoFill: any = {};
    const result = await (API as any).getLiveAgentConnectionInfo(params).catch(() => {});

    if (result) {
      const {
        user_type: userType,
        enquiry_type: enquiryType,
      } = result.auto_fill || {};
      let order = result.auto_fill?.order;
      const { min_desc_length, max_desc_length } = result.limitation;
      const isFlow1 = getProbingFormExperiment() === PROBING_FORM_EXP.Flow1;
      setMinDescLength(min_desc_length);
      setMaxDescLength(max_desc_length);

      if (isFlow1) {
        // flow1的时候，不自动填充 order
        order = {};
      }

      autoFill = {
        userType: userType?.id ? {
          value: userType?.id,
          content: userType?.name,
        } : undefined,
        enquiryType: enquiryType?.id ? {
          value: enquiryType?.id,
          content: enquiryType?.name,
        } : undefined,
        order: order?.order_sn ? formatOrder(order) : undefined,
      };
    }

    // 自动进线卡片带有的对列，优先级更高
    autoFill.userType = props.autoQueue?.userType || autoFill.userType;
    autoFill.enquiryType = props.autoQueue?.enquiryType || autoFill.enquiryType;

    return autoFill;
  };

  const onSubmit = async (formValues: ProbingFormValues) => {
    const files = formValues?.files || [];

    if (files.some(file => !file.url)) {
      toast.error(getTrans(PROBING_FORM_TRANSIFY.SUBMIT_WHEN_UPLOAD));
      return;
    }

    const params = {
      user_type: {
        id: formValues?.userType?.value || 0,
        name: formValues?.userType?.content || 'DEFAULT',
      },
      enquiry_type: {
        id: formValues?.enquiryType?.value || 0,
        name: formValues?.enquiryType?.content || 'DEFAULT',
      },
      problem_type: formValues?.problemType?.value ? {
        id: formValues?.problemType?.value,
        name: formValues?.problemType?.content,
      } : undefined,
      order_id: formValues?.order?.orderId,
      description: formValues?.description,
      files: formValues?.files,
    };
    reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_submit_total' }});
    const result = await (API as any).submitProbingForm(params).catch(() => {});

    if (result?.id) {
      await handleRequest('postMessage', {
        session_id: props.initStore?.sessionId,
        input_type: INPUT_TYPE.BUTTON,
        message_text: '',
        is_bypass: true,
        message_extra: { probing_form_params: { id: result.id }},
      });
      props.onSubmit(formValues as SubmitProbingFormParams);
      reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_submit_success' }});
    } else {
      toast.error(getTrans(PROBING_FORM_TRANSIFY.SUBMIT_FAILED));
      reporter.apiLog(
        `【Probing Form】submit failed, errormsg:${JSON.stringify(result)}`,
        { level: 'error' },
      );
    }
  };

  const componentTrackingData = {
    formItem: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.PROBING_FORM_FORM_ITEM],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PROBING_FORM_FORM_ITEM],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingItemData: (data: any) => ({
        field_id: data.fieldId,
        field_title: data.fieldName,
      }),
    },
    submitButton: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.PROBING_FORM_SUBMIT],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PROBING_FORM_SUBMIT],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingdataClick: (data: any) => ({
        is_clickable: !data.disabled,
        empty_fields_id: data.emptyFieldsId,
        empty_fields_title: data.emptyFieldsTitle,
        view_duration: Math.round((Date.now() - data.openFormTime) / 1000),
      }),
    },
    navbarBackButton: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.PROBING_FORM_BACK_BUTTON],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PROBING_FORM_BACK_BUTTON],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingdataClick: (data: any) => ({
        empty_fields_id: data.emptyFieldsId,
        empty_fields_title: data.emptyFieldsTitle,
        view_duration: Math.round((Date.now() - data.openFormTime) / 1000),
      }),
    },
    confirmModalButton: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.PROBING_FORM_CONFIRM],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PROBING_FORM_CONFIRM],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingItemData: (data: any) => ({
        yes_button: data.buttonType === 'confirm' ? 1 : 0,
        cancel_button: data.buttonType === 'cancel' ? 1 : 0,
      }),
    },
    userType: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.USER_TYPE_SELECTOR],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.USER_TYPE_SELECTOR],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingItemData: (data: any) => ({ button_name: data.content }),
    },
    enquiryType: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.ENQUIRY_TYPE_SELECTOR],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ENQUIRY_TYPE_SELECTOR],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingItemData: (data: any) => ({ button_name: data.content }),
    },
    selectOrderButton: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.INBOUND_ORDER_SELECTOR],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.INBOUND_ORDER_SELECTOR],
      trackingdataBasic: { chat_session_id: props.initStore?.sessionId as string },
      trackingItemData: (data: any) => ({ button_name: data.content }),
    },
  };

  useEffect(() => {
    props.inputStore?.inputRef?.current?.blur?.();
    props.inputStore?.UPDATE_INPUT_FOCUS?.(false);
    props.firstInit?.UPDATE_INPUT_FOCUS?.(false);

    reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_show_success' }});
    reporter.apiLog(
      '【Probing Form】show probing success',
      { level: 'debug' },
    );
  }, []);

  return (
    <UIProbingForm
      navbarProps={{
        title: getTrans(PROBING_FORM_TRANSIFY.TITLE),
        onBackButtonClick: props.onClose,
        showBoxShadow: false,
      }}
      skipValidateUserTypeAndEnquiryType={getProbingFormExperiment() === PROBING_FORM_EXP.Flow1}
      getInitialValues={getInitialValues}
      subtitle={getTrans(PROBING_FORM_TRANSIFY.SUBTITLE)}
      options={props.dataSource}
      userTabTypes={[
        { name: getTrans(BUYER_ORDER_LIST_TITLE_TEXT), type: USER_TYPE_INDEX.Buyer },
        { name: getTrans(SELLER_ORDER_LIST_TITLE_TEXT), type: USER_TYPE_INDEX.Seller },
      ]}
      loadMoreOrder={loadMoreOrder}
      limit={{
        minDescLength,
        maxDescLength,
      }}
      showFormItem={{ showUserAndEnquiryType, showOrderSelector }}
      formItemTitle={{
        userType: getTrans('enquiry_details_modal_category_title'),
        enquiryType: getTrans('enquiry_details_modal_option_title'),
        problemType: getTrans(PROBING_FORM_TRANSIFY.PROBLEM_TYPE),
        order: getTrans(PROBING_FORM_TRANSIFY.ORDER),
        description: getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION),
        files: getTrans(PROBING_FORM_TRANSIFY.SELECT_FILES),
      }}
      orderSelectorTitle={getTrans(SELECT_ORDER_TEXT)}
      descriptionPlaceholder={getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION_PROMPT)}
      minLimitPrompt={replaceParams(getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION_LIMIT), { count: minDescLength }) as string}
      minLimitPromptWhenError={replaceParams(getTrans(PROBING_FORM_TRANSIFY.DESCRIPTION_LIMIT_ERROR), { count: minDescLength }) as string}
      onSubmit={onSubmit}
      fileUploadSlot={ProbingFormUpload as any}
      confirmModal={{
        content: getTrans(PROBING_FORM_TRANSIFY.CONFIRM_CONTENT),
        cancelText: getTrans(PROBING_FORM_TRANSIFY.CONFIRM_NO),
        confirmText: getTrans(PROBING_FORM_TRANSIFY.CONFIRM_YES),
      }}
      submitButtonText={getTrans(PROBING_FORM_TRANSIFY.SUBMIT)}
      stillNotFoundButtonText={getTrans(SELECT_ORDER_CAN_NOT_FIND_TEXT)}
      stillNotFoundValueText={getTrans(PROBING_FORM_TRANSIFY.CANNOT_FIND_ORDER)}
      uploadFilePrompt={getTrans(PROBING_FORM_TRANSIFY.FILE_PROMPT)}
      componentTrackingData={componentTrackingData}
    />
  );
}));

export const waitProbingForm = (config: {
  showUserAndEnquiryType: boolean;
  showOrderSelector: boolean;
  dataSource: EnquiryItemType[];
  autoQueue?: ProbingFormProps['autoQueue'];
}) => {
  reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_show_total' }});
  reporter.apiLog(
    '【Probing Form】await show probing form',
    { level: 'debug' },
  );
  return new Promise<ProbingFormValues | {}>((resolve) => {
    const instance = show({
      children: (
        <ProbingForm
          showUserAndEnquiryType={config.showUserAndEnquiryType}
          showOrderSelector={config.showOrderSelector}
          onClose={() => {
            resolve({});
            instance.destroy();
          }}
          onSubmit={(probingForm: SubmitProbingFormParams) => {
            resolve(probingForm);
            instance.destroy();
          }}
          dataSource={config.dataSource}
          autoQueue={config.autoQueue}
        />
      ),
      onClose: () => {
        // resolve({});
        instance.destroy();
      },
    });
  });
};
